import React, { useEffect, useMemo, useState } from "react";
import { type GameState, type WaterManagement } from "../../vowGameLogic";
import {
  WATER_MANAGEMENT_CHOICES,
  WATER_MANAGEMENT_LABELS,
} from "../waterManagementOptions";

type SimulationStepProps = {
  phase: GameState["phase"];
  timerExpired: boolean;
  playerVote: WaterManagement | null;
  hostSelection: WaterManagement;
  onWaterManagementChange: (value: WaterManagement) => void;
};

const SimulationStep: React.FC<SimulationStepProps> = ({
  phase,
  timerExpired,
  playerVote,
  hostSelection,
  onWaterManagementChange,
}) => {
  const [draftSelection, setDraftSelection] = useState<WaterManagement | "">(
    playerVote ?? ""
  );
  const hasSubmittedVote = playerVote !== null;

  useEffect(() => {
    setDraftSelection(playerVote ?? "");
  }, [playerVote]);

  const isSimulationPhase = phase === "simulation";
  const hasPendingChange = playerVote !== draftSelection;
  const submitDisabled =
    !isSimulationPhase ||
    timerExpired ||
    draftSelection === "" ||
    !hasPendingChange;
  const helperMessage = useMemo(() => {
    if (!isSimulationPhase) {
      return "Voting opens after the host finalizes all actions.";
    }
    if (timerExpired) {
      return "Voting window closed. Waiting for the host to run the simulation.";
    }
    if (!hasSubmittedVote) {
      return "Choose a WM regime and submit when ready.";
    }
    if (!hasPendingChange) {
      return "Vote submitted. Change the selection to update your vote.";
    }
    return "Update your vote and press submit to send it to the host.";
  }, [hasPendingChange, hasSubmittedVote, isSimulationPhase, timerExpired]);

  return (
    <div id="simulation-step" className={isSimulationPhase ? "block" : "hidden"}>
      <h3 className="text-lg font-semibold text-slate-900">
        Step 2: Vote for Water Management
      </h3>
      <div className="mt-2 space-y-3 border-2 border-black bg-[#dcd2b5] p-4">
        <p className="text-xs text-slate-700">
          Cast your preferred WM regime. The host reviews every vote and can adopt the outcome or
          choose a different regime before the simulation runs.
        </p>
        <p className="text-xs text-slate-700">
          Current host selection:{" "}
          <span className="font-semibold text-slate-900">
            {WATER_MANAGEMENT_LABELS[hostSelection]}
          </span>
        </p>
        {timerExpired && (
          <p className="text-xs font-semibold text-rose-700">
            Voting timer expired — no further updates can be submitted.
          </p>
        )}
        <label htmlFor="wm-choice" className="block text-sm font-medium text-slate-800">
          Your vote:
        </label>
        <select
          id="wm-choice"
          value={draftSelection}
          onChange={(event) =>
            setDraftSelection(event.target.value as WaterManagement | "")
          }
          className="w-full border border-black bg-[#faf3e2] px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-black"
          disabled={!isSimulationPhase || timerExpired}
        >
          <option value="" disabled>
            Select a WM regime
          </option>
          {WATER_MANAGEMENT_CHOICES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (draftSelection === "") {
              return;
            }
            onWaterManagementChange(draftSelection);
          }}
          disabled={submitDisabled}
          className={`w-full ${
            submitDisabled
              ? "border-2 border-black bg-[#d1c7b0] px-3 py-2 text-sm font-semibold text-slate-700 cursor-not-allowed"
              : "border-2 border-black bg-[#f4d58d] px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-[#f0c85b]"
          }`}
        >
          Submit Vote
        </button>
        <p className="text-xs text-slate-700">{helperMessage}</p>
      </div>
    </div>
  );
};

export default SimulationStep;
